import { useMutation, useQuery } from "@apollo/client";
import { PlusIcon } from "@heroicons/react/outline";
import { compact, sortBy, uniqBy } from "lodash";
import {
  GetGoalGrowthConnectionsQueryQuery,
  GetGoalGrowthConnectionsQueryQueryVariables,
  GoalArtifactSidebarFragmentFragment,
  UpdateGoalGrowthConnectionsMutationMutation,
  UpdateGoalGrowthConnectionsMutationMutationVariables,
} from "types/graphql-schema";

import getCareerRoleRelatedDataQuery from "@apps/profile/career-tab/graphql/get-career-role-related-data-query";
import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull } from "@helpers/helpers";

import getGoalGrowthConnectionsQuery from "../graphql/get-goal-growth-connections-query";
import updateGoalGrowthConnectionsMutation from "../graphql/update-goal-growth-connections-mutation";
import GoalRoleGroup from "./goal-role-group";

const GoalGrowthConnectionList = ({
  goalArtifact,
  isReadOnly = false,
}: {
  goalArtifact: GoalArtifactSidebarFragmentFragment;
  isReadOnly?: boolean;
}) => {
  const label = useLabel();
  const goalOwner = goalArtifact?.owners?.edges[0]?.node;
  const { data, loading: loadingQuery } = useQuery<
    GetGoalGrowthConnectionsQueryQuery,
    GetGoalGrowthConnectionsQueryQueryVariables
  >(getGoalGrowthConnectionsQuery, {
    skip: !goalOwner,
    variables: { artifactId: goalArtifact.id, userId: goalOwner?.id ?? 0 },
  });
  const goal =
    data?.artifact && data.artifact.__typename === "GoalArtifactNode"
      ? data.artifact
      : null;

  const [updateGoalGrowthConnections, { loading: loadingMutation }] =
    useMutation<
      UpdateGoalGrowthConnectionsMutationMutation,
      UpdateGoalGrowthConnectionsMutationMutationVariables
    >(updateGoalGrowthConnectionsMutation);

  const goalCompetencies = goal?.competencyRelations
    ? assertEdgesNonNull(goal.competencyRelations)
    : [];
  const roles = uniqBy(
    goalCompetencies.map((relation) => relation.role).filter((role) => !!role),
    "id",
  );
  const rolesWithCompetencies = sortBy(
    roles.map((role) => ({
      orderValue: `${role.careerTrack.id}-${role.level}`,
      role,
      careerTrack: role.careerTrack,
      competencies: goalCompetencies
        .filter((relation) => relation.role?.id === role.id)
        .map((relation) => relation.competency),
    })),
    "orderValue",
  );

  const goalCompetencyRoleItemIds = goalCompetencies
    .map((relation) =>
      relation.role
        ? {
            competencyId: relation.competency.id,
            roleId: relation.role.id,
          }
        : null,
    )
    .filter((item) => !!item);

  const goalOwnerRoles = sortBy(
    (data?.user?.currentRoles ? data.user.currentRoles : []).map((role) => ({
      orderValue: `${role.careerTrack.id}-${role.level}`,
      ...role,
    })),
    "orderValue",
  );

  const handleAddCompetency = (roleId: number, competencyId: number) => {
    const newRelation = { competencyId, roleId };
    updateGoalGrowthConnections({
      variables: {
        artifactId: goalArtifact.id,
        additionalFields: {
          competencyIds: [...goalCompetencyRoleItemIds, newRelation],
        },
      },
      refetchQueries: [getCareerRoleRelatedDataQuery],
    });
  };

  if (!goalArtifact.canUpdate.permission || !goalOwner) {
    return null;
  }

  const roleOptions: DropdownOptionType[] = compact(
    goalOwnerRoles.map((role) => {
      const competencies = assertEdgesNonNull(role.competencies).filter(
        (competency) => {
          const hasMatchingCompetencyRole = goalCompetencyRoleItemIds.some(
            (item) =>
              item.competencyId === competency.id && item.roleId === role.id,
          );
          return !hasMatchingCompetencyRole;
        },
      );
      return competencies.length > 0
        ? {
            type: "submenu",
            label: `${role.title} (Level: ${role.level})`,
            options: [
              {
                type: "header",
                label: label("competency", {
                  titleCase: true,
                  pluralize: true,
                }),
              },
              ...competencies.map((competency) => ({
                label: competency.title,
                onClick: () => handleAddCompetency(role.id, competency.id),
              })),
            ],
          }
        : null;
    }),
  );

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="-mx-3 px-3 flex items-center justify-between">
        <div className="text-sm font-medium">
          Connected {label("competency", { titleCase: true, pluralize: true })}
        </div>
        {!isReadOnly && goalArtifact.canUpdate.permission && (
          <div className="flex items-center gap-2">
            {loadingMutation ? (
              <Loading mini size={5} />
            ) : (
              <Dropdown
                options={[
                  {
                    type: "header",
                    label: "Roles",
                  },
                  ...roleOptions,
                ]}
              >
                <Button
                  icon={PlusIcon}
                  theme={buttonTheme.iconGray}
                  tooltip={
                    roleOptions.length === 0
                      ? `No ${label("competency", {
                          pluralize: true,
                        })} available to connect.`
                      : `Add a ${label("competency")}`
                  }
                  disabled={roleOptions.length === 0}
                />
              </Dropdown>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 empty:hidden">
        {loadingQuery && !data ? (
          <Loading>Loading growth connections...</Loading>
        ) : rolesWithCompetencies.length > 0 ? (
          <div className="flex flex-col gap-6 text-sm">
            {rolesWithCompetencies.map(({ role, competencies }) => (
              <GoalRoleGroup
                key={role.id}
                role={role}
                goalOwnerId={goalOwner.id}
                goalId={goalArtifact.id}
                competencies={competencies}
                goalCompetencyRoleItemIds={goalCompetencyRoleItemIds}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GoalGrowthConnectionList;
